// CustomerUsersTable.jsx
import React, { useMemo, useState } from "react";
import {
    Box,
    Paper,
    Stack,
    Avatar,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableSortLabel,
    Pagination,
    FormControl,
    Select,
    MenuItem,
    Link as MuiLink,
} from "@mui/material";
import { USERS_FALLBACK } from "../../../utils/helpers";
import { ReactComponent as EditSvg } from "../../../assets/EditSvg.svg";
import { useNavigate } from "react-router";
import { withCurrentAccountPrefix } from "../../../utils/accountMode";
import { useStore } from "react-redux";

const UI = {
    border: "#E6E8EB",
    statusActive: "#1976d2",
    statusInactive: "#9E9E9E",
};

export default function UsersTab({ users = USERS_FALLBACK }) {

    const goMail = (email) => {
        // anchor default handles it; this is just a robust fallback
        window.location.href = `mailto:${encodeURIComponent(email)}`;
    };
    const goTel = (phone) => {
        const tel = sanitizePhone(phone);
        if (!tel) return;
        window.location.href = `tel:${tel}`;
    };

    const rows = useMemo(
        () =>
            (users || []).map((u, i) => ({
                id: i + 1,
                name: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
                email: u.email || "",
                mobile: u.mobile || "",
                landline: u.landline || "",
                status: u.status === "aktiv" ? "aktiv" : "deaktiviert",
                avatarUrl: u.avatarUrl || "",
            })),
        [users]
    );

    const [orderBy, setOrderBy] = useState("name");
    const [order, setOrder] = useState("asc");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const {i18n} = useStore().getState();

    const navigate = useNavigate();

    const handleSort = (column) => {
        if (orderBy === column) {
            setOrder(order === "asc" ? "desc" : "asc");
        } else {
            setOrderBy(column);
            setOrder("asc");
        }
    };


    const sanitizePhone = (s = "") =>
        s.replace(/[^\d+]/g, ""); // keep digits and + only


    const sorted = useMemo(() => {
        const copy = [...rows];
        copy.sort((a, b) => {
            const va = a[orderBy] ?? "";
            const vb = b[orderBy] ?? "";
            let c = 0;
            if (va < vb) c = -1;
            if (va > vb) c = 1;
            return order === "asc" ? c : -c;
        });
        return copy;
    }, [rows, orderBy, order]);

    const paged = useMemo(() => {
        const start = page * rowsPerPage;
        return sorted.slice(start, start + rowsPerPage);
    }, [sorted, page, rowsPerPage]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));

    const handleEdit = () => {
        navigate(withCurrentAccountPrefix("/edit-kunden-users"))
      }

    return (
        <div
   
            className="kundednetails-users-table"
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 500, color: "var(--color-primary)", letterSpacing: 0.2 }}
                >
                    User ({rows.length}) <span style={{ marginLeft:"15px",marginTop:"10px"}} className="cp" onClick={handleEdit}>{<EditSvg/>}</span>
                </Typography>
            </Box>

            <Table stickyHeader sx={{ px: 3 }}>
                <TableHead className="header">
                    <TableRow>
                        <TableCell sx={{ width: 40 }} >Status</TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={orderBy === "name"}
                                direction={orderBy === "name" ? order : "asc"}
                                onClick={() => handleSort("name")}
                            >
                                User
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={orderBy === "email"}
                                direction={orderBy === "email" ? order : "asc"}
                                onClick={() => handleSort("email")}
                            >
                                E-Mail
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={orderBy === "mobile"}
                                direction={orderBy === "mobile" ? order : "asc"}
                                onClick={() => handleSort("mobile")}
                            >
                                Handynummer
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={orderBy === "landline"}
                                direction={orderBy === "landline" ? order : "asc"}
                                onClick={() => handleSort("landline")}
                            >
                                Festnetz (optional)
                            </TableSortLabel>
                        </TableCell>

                    </TableRow>
                </TableHead>
                <TableBody>
                    {USERS_FALLBACK.map((u, idx) => {
                        const initials = `${u.firstName[0]}${u.lastName[0]}`;
                        const isActive = u.status === "aktiv";

                        const telMobile = sanitizePhone(u.mobile);
                        const telLandline = sanitizePhone(u.landline);
                        return (
                            <TableRow key={u.id} hover>

                                <TableCell >
                                    <Box sx={{ position: "relative", display: "inline-block" }}>
                                        {/* {u.avatarUrl ? (
                                            <Avatar
                                                src={u.avatarUrl}
                                                alt={`${u.firstName} ${u.lastName}`}
                                                sx={{ width: 48, height: 48 }}
                                            />
                                        ) : (
                                            <Avatar sx={{ width: 48, height: 48 }}>
                                                {initials}
                                            </Avatar>
                                        )} */}

                                        {/* status dot */}
                                        <Box
                                            sx={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: "50%",
                                                bgcolor: isActive ? "#1976d2" : "#9e9e9e",
                                                border: "2px solid white",
                                                position: "absolute",
                                                bottom: -8,
                                                left: 3,
                                            }}
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {u.firstName} {u.lastName}
                                </TableCell>

                                <TableCell>
                                    {u.email ? (
                                        <MuiLink
                                            component="a"
                                            href={`mailto:${u.email}`}
                                            underline="hover"
                                            color="primary"
                                            onClick={(e) => {
                                                // prevent parent row handlers; also force fallback
                                                e.stopPropagation();
                                                // allow browser to handle anchor; also set fallback:
                                                setTimeout(() => goMail(u.email), 0);
                                            }}
                                        >
                                            {u.email}
                                        </MuiLink>
                                    ) : (
                                        "—"
                                    )}
                                </TableCell>

                                <TableCell>
                                    {telMobile ? (
                                        <MuiLink
                                            component="a"
                                            href={`tel:${telMobile}`}
                                            underline="hover"
                                            color="primary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setTimeout(() => goTel(u.mobile), 0);
                                            }}
                                        >
                                            {u.mobile}
                                        </MuiLink>
                                    ) : (
                                        "—"
                                    )}
                                </TableCell>

                                <TableCell>
                                    {telLandline ? (
                                        <MuiLink
                                            component="a"
                                            href={`tel:${telLandline}`}
                                            underline="hover"
                                            color="primary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setTimeout(() => goTel(u.landline), 0);
                                            }}
                                        >
                                            {u.landline}
                                        </MuiLink>
                                    ) : (
                                        "—"
                                    )}
                                </TableCell>


                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 3,
                    borderTop: `1px solid ${UI.border}`,
                }}
            >
                <Pagination
                    count={totalPages}
                    page={page + 1}
                    onChange={(_, p) => setPage(p - 1)}
                    shape="rounded"
                />

                <FormControl variant="standard" sx={{ minWidth: 140 }}>
                    <Select
                        value={rowsPerPage}
                        onChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        sx={{ color: "rgba(0,0,0,.6)" }}
                    >
                        {[10, 25, 50].map((n) => (
                            <MenuItem key={n} value={n}>
                                {i18n?.customerPortal_entriesPerPage?.replace("%s", n)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </div>
    );
}
